import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useProjects,
  useCreateProject,
  useUpdateProject,
  useArchiveProject,
  ProjectList,
  ProjectForm,
  type Project,
} from "@project-mgmt/feature-projects";
import { Container, Page, Button, Modal } from "@project-mgmt/ui";
import { WorkspaceMetrics } from "../components/WorkspaceMetrics";
import { useWorkspacePermissions } from "../hooks/useSimplePermissions";
import { routes } from "../utils/routes";
import type { WorkspaceRouteParams } from "../types/routes";
import { useWorkspaceDetails } from "@project-mgmt/feature-workspaces";

export function ProjectsPage() {
  // Render tracking
  const renderCount = useRef(0);
  useEffect(() => {
    renderCount.current += 1;
    console.log(`[RENDER TRACKING] ProjectsPage rendered (count: ${renderCount.current})`, {
      timestamp: new Date().toISOString(),
    });
  });

  const navigate = useNavigate();
  const { workspaceId } = useParams() as WorkspaceRouteParams;
  const { data } = useWorkspaceDetails(workspaceId ?? "");

  const currentWorkspace = data?.workspace;

  const permissions = useWorkspacePermissions();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const { data: projectsData, isLoading, error } = useProjects(workspaceId ?? "");

  // Create project mutation
  const { trigger: createProject, isLoading: isCreating, error: createError } = useCreateProject();

  // Update project mutation
  const {
    trigger: updateProject,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateProject(selectedProject?.id ?? "", workspaceId ?? "");

  // Archive project mutation
  const { trigger: archiveProject, isLoading: isArchiving } = useArchiveProject(workspaceId ?? "");

  const projects = projectsData?.projects || [];

  // Handle project selection - updates URL and navigates to tasks
  const handleProjectSelect = (project: Project) => {
    if (workspaceId) {
      navigate(routes.projectTasks(workspaceId, project.id));
    }
  };

  // Handle project creation
  const handleCreateProject = async (projectData: any) => {
    try {
      const newProject = await createProject({
        ...projectData,
        workspaceId: currentWorkspace?.id,
      });

      setShowCreateForm(false);

      // Auto-select the new project
      if (newProject) {
        navigate(routes.projectTasks(workspaceId, newProject.id));
      }
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  // Handle project edit
  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setShowEditForm(true);
  };

  // Handle project update
  const handleUpdateProject = async (projectData: any) => {
    if (!selectedProject) return;

    try {
      await updateProject(projectData);
      setShowEditForm(false);
      setSelectedProject(null);
    } catch (error) {
      console.error("Failed to update project:", error);
    }
  };

  // Handle project archive - show confirmation modal
  const handleArchiveProject = (project: Project) => {
    setSelectedProject(project);
    setShowArchiveConfirm(true);
  };

  // Confirm and execute archive
  const confirmArchiveProject = async () => {
    if (!selectedProject) return;

    setShowArchiveConfirm(false);

    try {
      await archiveProject(selectedProject.id);
      setSelectedProject(null);
    } catch (error) {
      console.error("Failed to archive project:", error);
      setSelectedProject(null);
    }
  };

  return (
    <Container size="xl">
      <Page className="brand-page">
        {/* Header with Actions */}
        <div className="brand-page-hero mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <p className="brand-page-kicker">Delivery Pipeline</p>
            <h1 className="brand-page-title">
              Project <span className="brand-page-title-accent">Management</span>
            </h1>
            <p className="brand-page-subtitle truncate">Manage projects in {currentWorkspace?.name}</p>
          </div>

          <Button onClick={() => setShowCreateForm(true)} className="touch-target w-full sm:w-auto">
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Project
          </Button>
        </div>

        {/* Workspace Metrics */}
        <div className="brand-section-card mb-6 p-4 sm:p-6">
          <WorkspaceMetrics workspaceId={workspaceId} />
        </div>

        {/* Projects List */}
        <div className="brand-section-card p-4 sm:p-6">
          <ProjectList
            projects={projects}
            loading={isLoading || isArchiving}
            error={error?.message || null}
            onProjectView={handleProjectSelect}
            onProjectEdit={handleEditProject}
            onProjectArchive={handleArchiveProject}
            emptyMessage={
              permissions.canCreateProject
                ? "No projects yet. Create your first project to get started."
                : "No projects available in this workspace."
            }
          />
        </div>

        {/* Create Project Modal */}
        <Modal isOpen={showCreateForm} onClose={() => setShowCreateForm(false)} className="max-w-2xl">
          <ProjectForm
            onSubmit={handleCreateProject}
            onCancel={() => setShowCreateForm(false)}
            loading={isCreating}
            error={createError?.message || null}
          />
        </Modal>

        {/* Edit Project Modal */}
        <Modal
          isOpen={showEditForm}
          onClose={() => {
            setShowEditForm(false);
            setSelectedProject(null);
          }}
          className="max-w-2xl"
        >
          <ProjectForm
            project={selectedProject || undefined}
            onSubmit={handleUpdateProject}
            onCancel={() => {
              setShowEditForm(false);
              setSelectedProject(null);
            }}
            loading={isUpdating}
            error={updateError?.message || null}
          />
        </Modal>

        {/* Archive Confirmation Modal */}
        <Modal
          isOpen={showArchiveConfirm}
          onClose={() => {
            setShowArchiveConfirm(false);
            setSelectedProject(null);
          }}
          className="max-w-md"
        >
          <div className="p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Archive Project</h3>
                <p className="text-sm text-gray-500">This action can be undone later</p>
              </div>
            </div>

            <p className="mb-6 text-gray-700">
              Are you sure you want to archive <span className="font-semibold">"{selectedProject?.name}"</span>? The
              project and its tasks will be hidden from active views.
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowArchiveConfirm(false);
                  setSelectedProject(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmArchiveProject}
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
                disabled={isArchiving}
              >
                {isArchiving ? "Archiving..." : "Archive Project"}
              </Button>
            </div>
          </div>
        </Modal>
      </Page>
    </Container>
  );
}
